import $ from 'jquery';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';
import InternalLinkInput from 'notebook/mediocre/mentions/InternalLinkInput';
import LinkSearchDropdown from 'notebook/mediocre/mentions/LinkSearchDropdown';
import {LinkedItem} from 'ui-library/attach-dropdown/AttachDropdownConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Modal from 'ui-library/modals/Modal';
import isValidLink from 'util/isValidLink';
import sanitizeLink from 'util/sanitizeLink';
import {ElementKeyboardEvent, ElementMouseEvent, InputChangeEvent} from 'util/Types';

import {InternalLink as BaseInternalLink} from '../MediocreConstants';

interface ExternalLink {
  type: 'external';
  text: string;
  url: string | null;
}

interface InternalLink extends BaseInternalLink {
  type: 'internal';
}

export type Link = ExternalLink | InternalLink;

interface InsertLinkModalProps {
  fileId?: string;
  open: boolean;
  initialText?: string | null;
  initialUrl?: string | null;
  isExisting?: boolean;
  setLinkAndClose: (link: Link | null, linkedItem?: LinkedItem | null) => void;
}

interface State {
  text: string;
  url: string;
  query: string;
  showInvalidUrlError: boolean;
  internalLink?: InternalLink | null;
  linkedItem?: LinkedItem | null;
}

class InsertLinkModal extends React.Component<InsertLinkModalProps, State> {
  private textInputId = _.uniqueId('insert-link-modal-input');
  private urlInputId = _.uniqueId('insert-link-modal-input');

  static propTypes = {
    fileId: PropTypes.string,
    open: PropTypes.bool.isRequired,
    initialText: PropTypes.string,
    initialUrl: PropTypes.string,
    // setLinkAndClose takes a {text, url} link object. Calling setLinkAndClose(null) indicates the
    //   user cancelled the modal. Passing a null url indicates the user removed the link.
    setLinkAndClose: PropTypes.func.isRequired,
  };

  internalLinkInputRef = React.createRef<InternalLinkInput>();
  urlRef = React.createRef<HTMLInputElement>();
  linkSearchDropdownRef = React.createRef<LinkSearchDropdown>();

  _stateFromProps(props: InsertLinkModalProps): State {
    let url;
    const text = props.initialText || '';
    if (props.initialUrl) {
      // Check for falsy-ness, not existence, so that empty string does not count
      url = props.initialUrl;
    } else if (props.initialText && isValidLink(props.initialText) != null) {
      url = props.initialText;
    } else {
      url = '';
    }

    return {
      text,
      url,
      query: '',
      showInvalidUrlError: url.length > 0 && sanitizeLink(url) == null,
    };
  }

  state: State = this._stateFromProps(this.props);

  UNSAFE_componentWillReceiveProps(newProps: InsertLinkModalProps): void {
    if (!isEqual(newProps, this.props)) {
      this.setState(this._stateFromProps(newProps));
    }
  }

  isNewLink(): boolean {
    return this.props.initialUrl == null || this.props.initialUrl.length === 0;
  }

  saveLinkAndClose = (): void => {
    if (this.state.internalLink) {
      this.props.setLinkAndClose(this.state.internalLink, this.state.linkedItem);
    } else if (this.state.url === '') {
      this.removeLinkAndClose();
    } else {
      const sanitizedUrl = sanitizeLink(this.state.url);
      if (sanitizedUrl != null) {
        this.props.setLinkAndClose({type: 'external', text: this.state.text, url: sanitizedUrl});
      } else {
        this.setState({showInvalidUrlError: true});
      }
    }
  };

  removeLinkAndClose(): void {
    this.props.setLinkAndClose({type: 'external', text: this.state.text, url: null});
  }

  keepLinkAndClose = (): void => {
    // Use null to indicate the user cancelled; setLinkAndClose still restores the selection
    this.props.setLinkAndClose(null);
  };

  componentDidMount(): void {
    if (this.state.text.length === 0) {
      this.internalLinkInputRef.current!.focus();
    } else {
      $(ReactDOM.findDOMNode(this.urlRef.current)!).focus().select();
    }
  }

  // User modifies URL = remove the chip
  urlChangeHandler = (e: InputChangeEvent): void => {
    const url = e.target.value;
    if (isValidLink(url)) {
      this.setState({url, query: '', internalLink: null});
    } else {
      this.setState({url, query: url, internalLink: null});
    }
  };

  selectAttachment = (id: string, name: string, url: string, linkedItem: LinkedItem | null): void => {
    // Save id, name, and url of the selected attachment
    // If no initialText, replace text with the internal link chip, rendered in InternalLinkInput
    // If initialText exists (ELN text was highlighted), do not replace text with a chip
    const keepLink = this.props.initialText && this.state.text;
    const internalLink: InternalLink | null = keepLink
      ? null
      : {
          type: 'internal',
          id,
          name,
          url: benchlingUtil.parseUri(url).relative as string,
          iconId: linkedItem != null ? linkedItem.iconId : undefined,
        };
    this.setState({
      internalLink,
      url,
      text: keepLink ? this.state.text : '',
      query: '',
      linkedItem,
    });
  };

  updateText = (text: string): void => {
    this.setState({
      text,
      internalLink: null,
    });
  };

  keyDownHandler = (e: ElementKeyboardEvent): void => {
    if (this.linkSearchDropdownRef.current) {
      this.linkSearchDropdownRef.current.queryKeydownHandler(e);
    }
  };

  render(): JSX.Element {
    const allowSave = this.state.internalLink != null || this.state.text.length > 0;
    const buttons = _.compact([{text: 'Save', submit: true, disabled: !allowSave}]);
    return (
      <div
        onMouseDown={function (e: ElementMouseEvent): void {
          e.stopPropagation();
        }}
      >
        <Modal
          className="insertLinkModal"
          title={this.isNewLink() ? 'Add link' : 'Edit link'}
          onRequestClose={this.keepLinkAndClose}
          onRequestSubmit={this.saveLinkAndClose}
          buttons={buttons}
        >
          <div className="form-horizontal">
            <FormGroup>
              <Col sm={12}>
                <label className="u-tiny-spacing-bottom" htmlFor={this.textInputId}>
                  Text
                </label>
                <InternalLinkInput
                  id={this.textInputId}
                  ref={this.internalLinkInputRef}
                  link={this.state.internalLink}
                  updateText={this.updateText}
                  text={this.state.text}
                />
              </Col>
            </FormGroup>
            <FormGroup>
              <Col sm={12}>
                <label className="u-tiny-spacing-bottom" htmlFor={this.urlInputId}>
                  URL
                </label>
                {this.state.showInvalidUrlError ? (
                  <span className="u-danger-color u-tiny-spacing-left">This is not a valid link</span>
                ) : null}
                <input
                  id={this.urlInputId}
                  type="text"
                  value={this.state.url}
                  onChange={this.urlChangeHandler}
                  onKeyDown={this.keyDownHandler}
                  className="form-control"
                  ref={this.urlRef}
                  placeholder="Insert a hyperlink or search for a Benchling object"
                />
                {this.state.query !== '' ? (
                  <LinkSearchDropdown
                    ref={this.linkSearchDropdownRef}
                    width="100%"
                    fileId={this.props.fileId}
                    query={this.state.query}
                    selectAttachment={this.selectAttachment}
                  />
                ) : null}
              </Col>
            </FormGroup>
          </div>
        </Modal>
      </div>
    );
  }
}

export default InsertLinkModal;
